import React, { useContext, useState } from "react";
import {
    Container,
    Table,
    Button,
    InputGroup,
    FormControl,
    Collapse,
    Modal,
    Spinner,
} from "react-bootstrap";
import UploadModalContent from "./components/modal/upload-modal/upload-modal";
import "./report-container.scss";
import ReportsService from "../../../../../../services/reports-service";
import { IdentityContext } from "../../../../../../context/identity-context";

const ReportContainer = ({ items }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [openRows, setOpenRows] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [generateMessage, setGenerateMessage] = useState("");
    const [sendMessage, setSendMessage] = useState("");
    const [selectedReport, setSelectedReport] = useState(null);
    //const [selectedItem, setSelectedItem] = useState(null);
    const [checkedReportIndexes, setCheckedReportIndexes] = useState(new Set());
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [selectedComment, setSelectedComment] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
    const [sendingReports, setSendingReports] = useState(false);

    const reportService = new ReportsService();
    const { user } = useContext(IdentityContext);
    const makerName = user.attributes['custom:display_name'];

    const canGenerateReports = () => {
        return Array.from(checkedReportIndexes).every((id) => {
            const selectedItem = items.find(
                (item) => item.id.toString() === id
            );
            return (
                selectedItem.status === "Not Available"
            ); 
        });
    };

    const canSendReports = () => {
        return Array.from(checkedReportIndexes).every((itemId) => {
            const selectedItem = items.find(
                (item) => item.id.toString() === itemId
            );
            return (
                selectedItem.status === "Report Available" ||
                selectedItem.status === "For Adjustment"
            ); 
        });
    };

    const handleRowClick = (itemId) => {
        setOpenRows((prevOpenRows) =>
            prevOpenRows.includes(itemId)
                ? prevOpenRows.filter((id) => id !== itemId)
                : [...prevOpenRows, itemId]
        );
    };

    const handleUploadClick = (report) => {
        setSelectedReport(report);
        setShowUploadModal(true);
    };

    const handleCloseModal = () => {
        setShowUploadModal(false);
        setSelectedReport(null);
    };

    const handleCheckboxChange = (itemId, isChecked) => {
        const updatedIndexes = new Set(checkedReportIndexes);

        if (isChecked) {
            updatedIndexes.add(itemId);
        } else {
            updatedIndexes.delete(itemId);
        }

        setCheckedReportIndexes(updatedIndexes);

        
        const checkedItems = items.filter((item) =>
            updatedIndexes.has(item.id)
        );
    };

    const handleCommentClick = (comment) => {
        setSelectedComment(comment);
        setShowCommentModal(true);
    };

    // Handle the preview action
    const handlePreviewClick = async (report) => {
        try {
            const response = await reportService.retrieveReport(
                report.reportType,
                report.isin,
                report.reportTitle,
                report.issuerRequestDate
            );
            const newWindow = window.open(
                response.fileURL,
                "_blank",
                "noopener,noreferrer"
            );
            if (newWindow) newWindow.opener = null;
        } catch (error) {
            console.error("Error fetching report preview:", error.message);
        }
    };

    const handleDownloadClick = async (report) => {
        try {
            // Retrieve the report using the reportService
            const response = await reportService.retrieveReport(
                report.reportType,
                report.isin,
                report.reportTitle,
                report.issuerRequestDate
            );

            const url = response.fileURL;
            const filename =
                response.fileName || url.substring(url.lastIndexOf("/") + 1); // Use fileName from response


            const fileResponse = await fetch(url);
            if (!fileResponse.ok)
                throw new Error("Network response was not ok");

            const blob = await fileResponse.blob();
            const objectUrl = window.URL.createObjectURL(blob);

            // To download report
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = filename; // Use the correct filename
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            if (error.response) {
                console.error(
                    "Error fetching report download:",
                    error.response.data
                );
                console.error("Error status:", error.response.status);
                console.error("Error headers:", error.response.headers);
            } else {
                console.error("Error message:", error.message);
            }
        }
    };

    const handleGenerateClick = () => {
        const checkedReportsCount = checkedReportIndexes.size;
    
        if (checkedReportsCount > 0) {
            const reportPromises = Array.from(checkedReportIndexes).map((itemId) => {
                const selectedItem = items.find(
                    (item) => item.id.toString() === itemId
                );
                // console.log(selectedItem)
                // Adjust this to generate the report based on the item
                return reportService
                    .generateReport(
                        selectedItem.reportType, // Example of how to pass the report info, adjust as needed
                        selectedItem.reportTitle,
                        selectedItem.isin,
                        selectedItem.issuerRequestDate
                    )
                    .then((response) => {
                        // console.log("Report Generated: ", response);
                        return true;
                    })
                    .catch((error) => {
                        console.error("Error generating report: ", error);
                        return false;
                    });
            });
    
            Promise.all(reportPromises).then((results) => {
                const allSuccess = results.every((result) => result === true);
    
                if (allSuccess) {
                    setIsSuccess(true);
                    setGenerateMessage(
                        `Reports Successfully Generated\n${checkedReportsCount} reports generated.`
                    );
                } else {
                    setIsSuccess(false);
                    setGenerateMessage(
                        "There was an issue generating some reports."
                    );
                }
                setShowGenerateModal(true);
            });
        } else {
            setGenerateMessage(
                "No Reports Generated\nThere are no reports generated for selected items."
            );
            setShowGenerateModal(true);
        }
    };
    

    const handleSendClick = () => {
        setSendingReports(true);
        const checkedReportsCount = checkedReportIndexes.size;
        let successCount = 0;

        if (checkedReportsCount > 0) {
            const sendPromises = Array.from(checkedReportIndexes).map(
                (itemId) => {
                    const selectedItem = items.find(
                        (item) => item.id.toString() === itemId
                    );

                    // Send the report based on the item
                    return reportService
                        .sendReport(
                            selectedItem.isin,
                            selectedItem.reportTitle,
                            selectedItem.reportType,
                            selectedItem.issuerRequestDate,
                            makerName
                        )
                        .then((response) => {
                            // console.log("Report Sent: ", response);
                            successCount++;
                        })
                        .catch((error) => {
                            console.error("Error sending report: ", error);
                        });
                }
            );

            Promise.all(sendPromises)
                .then(() => {
                    setSendingReports(false);
                    if (successCount === checkedReportsCount) {
                        setIsSuccess(true);
                        setSendMessage(
                            `Reports Successfully Sent\n${checkedReportsCount} reports sent.`
                        );
                    } else {
                        setIsSuccess(false);
                        setSendMessage(
                            `Some reports were not sent. Successfully sent ${successCount} out of ${checkedReportsCount}.`
                        );
                    }
                    setShowSendModal(true);
                })
                .catch(() => {
                    setSendingReports(false);
                });
        } else {
            setSendMessage(
                "No Reports Sent\nThere are no reports sent for selected items."
            );
            setShowSendModal(true);
        }
    };

    const handleGenerateModalClose = () => {
        setShowGenerateModal(false);
        if (isSuccess) {
            window.location.reload(); // Refresh only if success
        }
    };

    const handleSendModalClose = () => {
        setShowSendModal(false);
        setSendingReports(false);
        if (isSuccess) {
            window.location.reload(); // Refresh only if success
        }
    };

    // Sorting function
    const sortedItems = [...items].sort((a, b) => {
        const dateA = new Date(a.issuerRequestDate);
        const dateB = new Date(b.issuerRequestDate);
        if (sortConfig.direction === "asc") {
            return dateA - dateB;
        } else {
            return dateB - dateA;
        }
    });

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const filteredItems = sortedItems.filter((item) => {
        const reportTitle = item.reportTitle ? item.reportTitle.toLowerCase() : "";
        const securityIssuer = item.securityIssuer ? item.securityIssuer.toLowerCase() : "";
        const securityName = item.securityName ? item.securityName.toLowerCase() : "";
        const isin = item.isin ? item.isin.toLowerCase() : "";
        const issuerRequestDate = item.issuerRequestDate ? item.issuerRequestDate.toLowerCase() : "";
        const status = item.status ? item.status.toLowerCase() : "";

        // const issuer = item.issuer ? item.issuer.toLowerCase() : "";
        // const name = item.name ? item.name.toLowerCase() : "";
        // const isin = item.isin ? item.isin.toLowerCase() : "";
        // const paymentDate = item.paymentDate
        //     ? item.paymentDate.toLowerCase()
        //     : "";
        // const status = item.status ? item.status.toLowerCase() : "";
        const searchLower = searchTerm.toLowerCase();

        return (
            reportTitle.includes(searchLower) ||
            securityIssuer.includes(searchLower) ||
            securityName.includes(searchLower) ||
            isin.includes(searchLower) ||
            issuerRequestDate.includes(searchLower) ||
            status.includes(searchLower)
        );
    });

    return (
        <Container className="report-container">
            {/* Search Bar */}
            <InputGroup className="mb-3">
                <FormControl
                    placeholder="Search..."
                    aria-label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>

            {/* Content Area */}
            <div className="content-wrapper">
                <div className="content-scrollable">
                    <Table bordered>
                        <thead>
                            <tr>
                                <th></th>
                                <th>REPORT NAME</th>
                                <th>ISSUER</th>
                                <th>SECURITY NAME</th>
                                <th>ISIN</th>
                                <th
                                    onClick={() => handleSort("paymentDate")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Date of Request{" "}
                                    {sortConfig.key === "paymentDate"
                                        ? sortConfig.direction === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th>STATUS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.map((item) => (
                                <React.Fragment key={item.id}>
                                    <tr onClick={() => handleRowClick(item.id)}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={checkedReportIndexes.has(
                                                    item.id
                                                )} // Check if the item is selected
                                                onChange={(e) =>
                                                    handleCheckboxChange(
                                                        item.id,
                                                        e.target.checked
                                                    )
                                                } // Handle checkbox change
                                                disabled={
                                                    item.status !==
                                                        "Report Available" &&
                                                    item.status !==
                                                        "Not Available" &&
                                                    item.status !==
                                                        "For Adjustment"
                                                }
                                            />
                                        </td>
                                        <td>{item.reportTitle}</td>
                                        <td>{item.securityIssuer}</td>
                                        <td>{item.securityName}</td>
                                        <td>{item.isin}</td>
                                        <td>{item.issuerRequestDate}</td>
                                        <td>
                                            {item.status}
                                            {item.comment ? (
                                                <span
                                                    style={{
                                                        cursor: "pointer",
                                                        marginLeft: "5px",
                                                    }}
                                                    onClick={() =>
                                                        handleCommentClick(
                                                            item.comment
                                                        )
                                                    }
                                                >
                                                    ▼
                                                </span>
                                            ) : (
                                                ""
                                            )}
                                        </td>
                                        <td>
                                            <>
                                                {(item.status === "Report Available" ||
                                                item.status === "For Adjustment") && (
                                                <Button
                                                    variant="primary"
                                                    className="action-button"
                                                    onClick={() => handleUploadClick(item)}
                                                    disabled={item.status === "Report Empty"}
                                                >
                                                    Upload
                                                </Button>
                                                )}
                                                <Button
                                                variant="primary"
                                                className="action-button"
                                                onClick={() => handlePreviewClick(item)}
                                                disabled={item.status === "Report Empty" || 
                                                    item.status === "Not Available"}
                                                >
                                                Preview
                                                </Button>
                                                <Button
                                                variant="primary"
                                                className="action-button"
                                                onClick={() => handleDownloadClick(item)}
                                                disabled={item.status === "Report Empty" || 
                                                            item.status === "Not Available"
                                                }
                                                >
                                                Download
                                                </Button>
                                            </>
                                            </td>

                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
                <Button
                    variant="secondary"
                    className="me-2"
                    onClick={handleGenerateClick}
                    disabled={!canGenerateReports()}
                >
                    Generate Reports
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSendClick}
                    disabled={!canSendReports() || sendingReports}
                >
                    {sendingReports ? (
                        <>
                            <Spinner
                                animation="border"
                                style={{ width: "20px", height: "20px" }}
                            ></Spinner>
                        </>
                    ) : (
                        "Send Reports"
                    )}
                </Button>
            </div>

            {/* Upload Modal */}
            {selectedReport && (
                <Modal
                    show={showUploadModal}
                    onHide={handleCloseModal}
                    dialogClassName="maker-upload-modal modal-dialog-centered"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Upload Document</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <UploadModalContent
                            context={{ report: selectedReport }}
                            onClose={handleCloseModal}
                        />
                    </Modal.Body>
                </Modal>
            )}

            {/* Generate Report Modal */}
            <Modal
                show={showGenerateModal}
                onHide={handleGenerateModalClose}
                backdrop="static"
                dialogClassName="generate-modal modal-dialog-centered"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Generate Report</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{generateMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleGenerateModalClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Send Report Modal */}
            <Modal
                show={showSendModal}
                onHide={handleSendModalClose}
                backdrop="static"
                dialogClassName="send-report-modal modal-dialog-centered"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Send Report</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{sendMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleSendModalClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Comment Modal */}
            <Modal
                show={showCommentModal}
                onHide={() => setShowCommentModal(false)}
                backdrop="static"
                dialogClassName="commentModal modal-dialog-centered"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Report Comment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{selectedComment ? selectedComment : "No Comments"}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowCommentModal(false)}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ReportContainer;
